import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const OVERFLOW_TOLERANCE = 2;

/** A studio region with a visible, persistent way to reach content below it. */
const StudioScrollPane = forwardRef(function StudioScrollPane({
  children,
  colors,
  style,
  contentContainerStyle,
  testID,
  accessibilityLabel = 'Studio content',
  moreLabel = 'More below',
  resetKey,
  onLayout,
  onContentSizeChange,
  onScroll,
  ...scrollProps
}, forwardedRef) {
  const scrollRef = useRef(null);
  const [paneHeight, setPaneHeight] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [offset, setOffset] = useState(0);

  const attachRef = useCallback((node) => {
    scrollRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  // Compare against the whole pane, before its footer. Otherwise content that
  // shrinks enough to fit can keep a now-unnecessary footer alive indefinitely.
  const hasOverflow = paneHeight > 0 && contentHeight > paneHeight + OVERFLOW_TOLERANCE;
  const maxOffset = Math.max(0, contentHeight - (viewportHeight || paneHeight));
  const atBottom = offset > 0 && offset >= maxOffset - OVERFLOW_TOLERANCE;

  useEffect(() => {
    scrollRef.current?.scrollTo({ y: 0, animated: false });
    setOffset(0);
  }, [resetKey]);

  useEffect(() => {
    if (!paneHeight) return;
    const nextOffset = hasOverflow ? Math.min(offset, maxOffset) : 0;
    if (nextOffset !== offset) {
      scrollRef.current?.scrollTo({ y: nextOffset, animated: false });
      setOffset(nextOffset);
    }
  }, [hasOverflow, maxOffset, offset, paneHeight]);

  const handleLayout = useCallback((event) => {
    setViewportHeight(event.nativeEvent.layout.height);
    onLayout?.(event);
  }, [onLayout]);

  const handleContentSizeChange = useCallback((width, height) => {
    setContentHeight(height);
    onContentSizeChange?.(width, height);
  }, [onContentSizeChange]);

  const handleScroll = useCallback((event) => {
    setOffset(Math.max(0, event.nativeEvent.contentOffset.y));
    onScroll?.(event);
  }, [onScroll]);

  const advance = () => {
    const target = atBottom
      ? 0
      : Math.min(maxOffset, offset + (viewportHeight || paneHeight) * 0.75);
    scrollRef.current?.scrollTo({ y: target, animated: true });
  };

  return (
    <View
      style={[styles.container, style]}
      onLayout={(event) => setPaneHeight(event.nativeEvent.layout.height)}
    >
      <ScrollView
        {...scrollProps}
        ref={attachRef}
        testID={testID}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole={Platform.OS === 'web' ? 'region' : undefined}
        tabIndex={Platform.OS === 'web' ? 0 : undefined}
        style={styles.scroll}
        contentContainerStyle={contentContainerStyle}
        showsVerticalScrollIndicator
        persistentScrollbar
        scrollEventThrottle={32}
        onLayout={handleLayout}
        onContentSizeChange={handleContentSizeChange}
        onScroll={handleScroll}
      >
        {children}
      </ScrollView>
      {hasOverflow ? (
        <View style={[
          styles.footer,
          { backgroundColor: colors?.solid_color_block, borderTopColor: colors?.border_color_secondary },
        ]}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={atBottom ? 'Back to top' : moreLabel}
            accessibilityHint={`Scrolls ${atBottom ? 'to the start of' : 'down in'} ${accessibilityLabel}`}
            onPress={advance}
            style={({ hovered, pressed }) => [
              styles.control,
              (hovered || pressed) && { backgroundColor: colors?.solid_color_tertiary },
            ]}
          >
            <Icon name={atBottom ? 'arrow-up' : 'arrow-down'} size={11} color={colors?.text_color_secondary} />
            <Text size="xs" weight="medium" color="secondary">{atBottom ? 'Back to top' : moreLabel}</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  scroll: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    ...(Platform.OS === 'web' ? { scrollbarGutter: 'stable' } : {}),
  },
  footer: {
    flexShrink: 0,
    borderTopWidth: 1,
  },
  control: {
    minHeight: 32,
    paddingHorizontal: 12,
    paddingVertical: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },
});

export default StudioScrollPane;
